import {
  PATH_DEFAULT_STORE_PROJECT_ASSIGNEES_EDIT,
  PATH_DEFAULT_STORE_PROJECT_ASSIGNEES_NEW,
} from "@/constants";
import { defaultStoreProjectAssigneeListItem } from "@/schemas/defaultStoreProjectAssignee";
import { DefaultStoreProjectAssigneeListItem } from "@/types/defaultStoreProjectAssignee";
import { memo, useMemo, useState, useCallback } from "react";
import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import { RowSelectionProvider } from "@/components/data-table/context/RowSelectionProvider";
import { useRowSelectionContext } from "@/components/data-table/context/RowSelectionContext";
import { List } from "@/components/list/List";
import { useDefaultStoreProjectAssigneeListQuery } from "@/modules/defaultStoreProjectAssignees/hooks/useDefaultStoreProjectAssigneeListQuery";
import { useDefaultStoreProjectAssigneeBreadcrumbs } from "@/modules/defaultStoreProjectAssignees/hooks/useDefaultStoreProjectAssigneeBreadcrumbs";
import { useDefaultStoreProjectAssigneeColumnOverrides } from "@/modules/defaultStoreProjectAssignees/hooks/useDefaultStoreProjectAssigneeColumnOverrides";
import { Module } from "@/constants";
import { toCamelCase } from "@/lib/utils";
import AddItemButton from "@/components/AddItemButton";
import PermissionGuard from "@/components/PermissionGuard";
import { Operation } from "@/types/permissions";
import { DefaultStoreProjectAssigneesDeleteSelectedButton } from "./DefaultStoreProjectAssigneesDeleteSelectedButton";
import { DefaultStoreProjectAssigneesDeleteDialog } from "./DefaultStoreProjectAssigneesDeleteDialog";
import { generatePath } from "react-router-dom";

const DefaultStoreProjectAssigneesDirectoryContent = () => {
  const { store } = useRowSelectionContext();
  const { query: defaultStoreProjectAssigneeListQuery, options } =
    useDefaultStoreProjectAssigneeListQuery();

  const breadcrumbs = useDefaultStoreProjectAssigneeBreadcrumbs();
  const columnOverrides = useDefaultStoreProjectAssigneeColumnOverrides();
  const name = toCamelCase(Module.DefaultStoreProjectAssignees);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const actions = useMemo(
    () => [
      <PermissionGuard permission={Operation.Create} key="add">
        <AddItemButton
          path={PATH_DEFAULT_STORE_PROJECT_ASSIGNEES_NEW}
          labelKey={`${name}.add`}
        />
      </PermissionGuard>,
    ],
    [name],
  );

  const handleDeleteClick = useCallback(() => {
    setIsDeleteDialogOpen(true);
  }, []);

  const handleDeleteSuccess = useCallback(() => {
    store.clear();
    defaultStoreProjectAssigneeListQuery.refetch();
  }, [store, defaultStoreProjectAssigneeListQuery]);

  const selectedIds = Array.from(store.getSelection());

  return (
    <List<DefaultStoreProjectAssigneeListItem>
      schema={defaultStoreProjectAssigneeListItem}
      name={name}
      pathBuilder={(row) =>
        generatePath(PATH_DEFAULT_STORE_PROJECT_ASSIGNEES_EDIT, {
          assigneeId: row.id,
        })
      }
      query={defaultStoreProjectAssigneeListQuery}
      options={options}
      columnOverrides={columnOverrides}
      tableFeatures={{
        enableRowSelection: true,
        enableSelectAll: true,
        exportSettings: { moduleName: Module.DefaultStoreProjectAssignees },
      }}
    >
      <List.Header
        breadcrumbLabel={`${name}.title.multiple`}
        breadcrumbs={breadcrumbs}
        actions={actions}
      />
      <List.Menubar>
        <List.Menubar.Tools />
        <List.Menubar.Columns />
        <List.Menubar.Reset />
      </List.Menubar>
      <DataTableQueryContextProvider
        value={{ query: defaultStoreProjectAssigneeListQuery }}
      >
        <List.Content>
          <List.Status>
            <List.SelectedRowsStatus />
            {!defaultStoreProjectAssigneeListQuery.isLoading && (
              <DefaultStoreProjectAssigneesDeleteSelectedButton
                onClick={handleDeleteClick}
              />
            )}
          </List.Status>
          <DefaultStoreProjectAssigneesDeleteDialog
            open={isDeleteDialogOpen}
            onOpenChange={setIsDeleteDialogOpen}
            selectedIds={selectedIds}
            onDeleteSuccess={handleDeleteSuccess}
          />
        </List.Content>
      </DataTableQueryContextProvider>
    </List>
  );
};

const DefaultStoreProjectAssigneesDirectoryComp = () => {
  return (
    <RowSelectionProvider>
      <DefaultStoreProjectAssigneesDirectoryContent />
    </RowSelectionProvider>
  );
};

export const DefaultStoreProjectAssigneesDirectory = memo(
  DefaultStoreProjectAssigneesDirectoryComp,
);
